'use client';

import { useGetViewupSnsAccounts } from 'Shared/hooks/api';
import { useTokenStore } from 'Shared/hooks/useTokenStore';
import { Analytics, createReturnUrl } from 'Shared/utils/client';
import { LINK } from '@constants';
import {
  Box,
  CloseLine,
  Column,
  Icon,
  InfoBanner,
  Row,
  Typography,
  ViewingMainPictogram,
} from '@viewup/ui';
import { useGlobalModalStore } from 'app/_hook/useGlobalModalStore';
import { useGetRewardPointMap } from 'app/viewing/_entities/common';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Fragment, useEffect, useState } from 'react';
import { getLocalStorage, removeLocalStorage, setLocalStorage } from 'viewup-lib/common';
import { ViewingType } from '../../_entities/common';
import { OffViewingModal } from '../../_features/click';
import { ViewingMenu, viewingMenus } from '../../_modules/common/ViewingMenu';
import { ViewupGuideBanner } from '../../_modules/common/ViewupGuideBanner';

const CLICK_BANNER_KEY = 'clickViewingBanner';
const POST_BANNER_KEY = 'litePostBanner';

export const ViewingMainClientPage = () => {
  const router = useRouter();
  const { accessToken } = useTokenStore();
  const { data: pointMap } = useGetRewardPointMap();
  const { data: getViewupAccounts } = useGetViewupSnsAccounts();

  const { getValue } = useGlobalModalStore();
  const { setConfirmModal, setAlertModal } = getValue('actions');

  const [isOpenAutoViewingModal, setIsOpenAutoViewingModal] = useState(false);
  const [tutorialImage, setTutorialImage] = useState<boolean>(false);

  const hasAccounts = (getViewupAccounts?.data?.length || 0) > 0;

  useEffect(() => {
    removeLocalStorage(CLICK_BANNER_KEY);
    if (getLocalStorage(POST_BANNER_KEY)) return;
    setTutorialImage(true);
  }, []);

  const checkLoggedIn = (e: React.MouseEvent<HTMLElement, MouseEvent>, type: string) => {
    if (!accessToken) {
      e.preventDefault();
      Analytics.getInstance().track('NONLOG_VIEWING_MAIN_MENU', {
        viewingType: type,
      });
      setConfirmModal({
        content: `본 서비스는 회원만 이용이 가능해요.\n로그인할까요?`,
        onClickFillButton: () => {
          router.push(`${LINK.LOGIN}${createReturnUrl(LINK.VIEWING)}`);
        },
      });

      return false;
    }
    Analytics.getInstance().track('VIEWING_MAIN_MENU', { type });
  };

  const handleClickAutoViewing = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    checkLoggedIn(e, 'off');
    if (!accessToken) return;
    setIsOpenAutoViewingModal(true);
  };

  const handleClickMacroViewing = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    checkLoggedIn(e, 'auto');
    if (!accessToken) return;
    if (!hasAccounts) {
      e.preventDefault();
      setAlertModal({
        actionButtonText: '계정 추가하기',
        content: '오토뷰업으로 설정할 수 있는\n계정이 없어요.',
        onClickActionButton: () => {
          router.push(LINK.ACCOUNT_ADD);
        },
        title: '계정을 추가해 주세요.',
      });
    }
  };

  const handleClickTutorialClose = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    setTutorialImage(false);
    setLocalStorage(POST_BANNER_KEY, true);
  };

  const getPointInfo = (type: string) => {
    if (!pointMap?.[type]) return '0';
    const { min, max } = pointMap[type];

    if (min === max) return `${min}`;
    return `${min} ~ ${max}`;
  };

  return (
    <Column bgColor='bg.infoSubtle' gap={24} height={'100%'} px={16} py={24}>
      <Row alignItems='center' justifyContent='space-between'>
        <Column gap={8}>
          <Typography
            as='h2'
            color='text.primary'
            css={{ whiteSpace: 'break-spaces' }}
            fontType='header2.medium'
          >{`지금 바로\n참여할 수 있는 뷰업 👀`}</Typography>
          <Typography color='text.secondary' fontType='body2.regular'>
            새로운 캠페인이 도착했어요!
          </Typography>
        </Column>
        <ViewingMainPictogram />
      </Row>

      {tutorialImage && (
        <InfoBanner
          colorVariant='green'
          endIcon={
            <Icon
              css={{ cursor: 'pointer' }}
              onClick={handleClickTutorialClose}
              size='3XS'
              source={CloseLine}
            />
          }
          onClick={() => {
            router.push(LINK.VIEWING_POST_APPLY_LIST);
          }}
        >
          <Row css={{ cursor: 'pointer' }} gap={4}>
            <Typography color='text.success' fontType='body2.bold'>
              NEW!
            </Typography>

            <Typography color='text.primary' fontType='body2.regular'>
              포스트뷰업에{' '}
              <Typography color='text.primary' fontType='body2.bold'>
                다양한 유형
              </Typography>
              이 추가됐어요!
            </Typography>
          </Row>
        </InfoBanner>
      )}

      <Box css={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }} gap={16}>
        {viewingMenus.map((menu) => (
          <Fragment key={menu.type}>
            {menu.link ? (
              <Link
                data-tour={`viewing-menu-${menu.type}`}
                href={menu.link}
                {...(!(menu.type === ViewingType.CLICK || menu.type === ViewingType.POST) && {
                  onClick: (e) => checkLoggedIn(e, menu.type),
                })}
                {...(menu.type === ViewingType.MACRO && {
                  onClick: handleClickMacroViewing,
                })}
              >
                {<ViewingMenu key={menu.type} {...menu} point={getPointInfo(menu.type)} />}
              </Link>
            ) : (
              <div data-tour={`viewing-menu-${menu.type}`} onClick={handleClickAutoViewing}>
                <ViewingMenu key={menu.title} {...menu} point={getPointInfo(menu.type)} />
              </div>
            )}
          </Fragment>
        ))}
      </Box>

      <ViewupGuideBanner onClick={() => window.open(LINK.NOTION_VIEWUP_GUIDE, '_blank')} />

      {isOpenAutoViewingModal && (
        <OffViewingModal
          exitHandler={() => {
            setIsOpenAutoViewingModal(false);
          }}
          isOpen={isOpenAutoViewingModal}
        />
      )}

      {/* {tutorialImage && (
        <Box
          bgColor='bg.dimmer.60'
          css={{ zIndex: '99999' }}
          height={'100%'}
          position='fixed'
          right={0}
          top={0}
          width={'100%'}
        >
          <Icon
            color='icon.interactive.inverse'
            css={{ cursor: 'pointer', zIndex: '99999' }}
            position='absolute'
            right={24}
            size='XS'
            source={CloseLine}
            top={24}
            onClick={handleClickTutorialClose}
          />

          <Box
            position='absolute'
            css={{
              top: `${SIZE.HEADER_HEIGHT}px`,
              maxHeight: 700,
              overflowY: 'hidden',
            }}
          >
            <img alt='클릭뷰잉안내' src={tutorialImage} style={{ width: '100%' }} />
          </Box>
        </Box>
      )} */}
    </Column>
  );
};
